<!-- Script editor (#scriptEditorModal, z-60, full screen; teleported into #scriptEditorHost, where the legacy markup was in
     index.html): top bar (back, pen icon, title, Visual / Markdown / Fountain, save status, comments button with count, save as template,
     delete), tabs (ScriptTabs.vue: a bar above the page on small screens, a column left of the outline from 1024 px), toolbar (EditorToolbar.vue, with its drop-down ToolbarMenu.vue in #tbDropHost),
     outline (ScriptToc.vue), the page, the comments column (CommentsPanel.vue), the Markdown field, the Fountain mode
     (FountainEditor.vue: its bar, scenes and characters, text field and preview), the footer (counter, Teleprompter,
     Copy, .md; in Fountain mode .fountain). State and actions: the editor store (src/stores/editor.ts) and the toc, comments
     and toolbar stores; the data side is the scripts store.
     The WRITING SURFACE (src/features/editor/surface/): #scriptPageWrap with #scriptBody (contenteditable), #blockHandle
     and #blockDrop is static markup in index.html (#scriptSurface), whose listeners main.ts installs before the Vue app
     (installEditorSurface, in the legacy order); this component moves it into the page once mounted (into an empty
     `display: contents` box, so the layout is the same) and never draws inside it. Always mounted (never v-if): the
     surface's document listeners look for its elements at any time, and opening must show the page in the same moment
     (the page takes the focus):
     the hidden class of the dialog, of the page and of the Markdown field follows the store synchronously.
     Registered in the dialog stack while open (editor store setShown: `editing`, no Esc; Esc and Ctrl+S are its own
     steps in src/features/keyboard.ts). -->
<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from '../../composables/useI18n'
import { useEditorStore } from '../../stores/editor'
import { useCommentsStore } from '../../stores/comments'
import { useTocStore } from '../../stores/toc'
import { useTeleprompterStore } from '../../stores/teleprompter'
import { placeSelMenu } from '../../features/editor/surface/selmenu'
import { hideBlockMenu } from '../../features/editor/surface/blocks'
import { placeLinkBubble } from '../../features/editor/surface/links'
import ScriptTabs from '../editor/ScriptTabs.vue'
import EditorToolbar from '../editor/EditorToolbar.vue'
import ToolbarMenu from '../editor/ToolbarMenu.vue'
import ScriptToc from '../editor/ScriptToc.vue'
import CommentsPanel from '../editor/CommentsPanel.vue'
import FountainEditor from '../editor/FountainEditor.vue'

const { t } = useI18n()
const ed = useEditorStore()
const comments = useCommentsStore()
const toc = useTocStore()
const prompter = useTeleprompterStore()

const root = ref<HTMLElement | null>(null)
const visualWrap = ref<HTMLElement | null>(null)
const pageSlot = ref<HTMLElement | null>(null)
const md = ref<HTMLTextAreaElement | null>(null)
const mdWrap = ref<HTMLElement | null>(null)

/** Visual / Markdown / Fountain switch buttons */
const mc = (a: boolean) => 'px-3 py-1 rounded-md ' + (a ? 'bg-brand text-white' : 'text-gray-400')

// The editor, the page and the Markdown field appear and disappear at once (the legacy code toggled their classes). The
// mode switch is a tab bar: the visual page, the Markdown box and the Fountain editor are its tab panels, so each one
// plays the tab entrance when it appears (legacy.css)
watch(() => ed.shown, on => { root.value?.classList.toggle('hidden', !on) }, { flush: 'sync' })
watch(() => ed.shownMode, m => {
  visualWrap.value?.classList.toggle('hidden', m !== 'visual')   // the toolbar: EditorToolbar.vue; Fountain: FountainEditor.vue
  md.value?.classList.toggle('hidden', m !== 'md')
  mdWrap.value?.classList.toggle('hidden', m !== 'md')
}, { flush: 'sync' })

/** Scrolling the page: the selection menu follows, the outline's active heading, the block menu closes, the link
 *  bubble follows */
function onScroll(): void {
  placeSelMenu()
  toc.onScroll(); hideBlockMenu(); placeLinkBubble()
}
/** Markdown field: Tab = two spaces */
function mdKey(e: KeyboardEvent): void {
  if (e.key !== 'Tab') return
  e.preventDefault()
  const ta = e.target as HTMLTextAreaElement, p = ta.selectionStart
  ta.setRangeText('  ', p, ta.selectionEnd, 'end'); ed.queueSave()
}

let surface: HTMLElement | null = null
onMounted(() => {
  // The writing surface (static markup with its listeners, src/features/editor/surface/) goes into the page
  surface = document.getElementById('scriptPageWrap')
  if (surface && pageSlot.value) pageSlot.value.appendChild(surface)
  window.addEventListener('keydown', comments.escListener, true)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', comments.escListener, true)
  const park = document.getElementById('scriptSurface')
  if (surface && park) park.appendChild(surface)   // back to its place, with its listeners (hot reload)
})
</script>

<template>
  <Teleport to="#scriptEditorHost">
    <div id="scriptEditorModal" ref="root" class="hidden fixed inset-0 bg-darkbg z-[60] flex flex-col">
      <div class="flex items-center gap-2 sm:gap-3 px-4 md:px-6 py-3 border-b border-gray-800 bg-darkcard">
        <button class="text-gray-400 hover:text-gray-100 text-lg" @click="ed.closeEditor()"><i class="fas fa-chevron-left"></i></button>
        <span class="hidden sm:flex w-8 h-8 rounded-lg bg-brand/15 text-brand items-center justify-center flex-shrink-0" aria-hidden="true"><i class="fas fa-pen-nib"></i></span>
        <input id="scriptTitle" v-model="ed.title" :placeholder="t('Script title')" class="flex-1 min-w-0 bg-transparent font-bold text-base text-gray-100 focus:outline-none" @input="ed.queueSave()">
        <div role="tablist" class="flex bg-darkbtn rounded-lg p-0.5 text-xs font-bold"><button id="modeVis" role="tab" :aria-selected="ed.shownMode === 'visual'" :title="t('Visual')" :class="mc(ed.shownMode === 'visual')" @click="ed.setMode('visual')"><i class="fas fa-pen-nib sm:hidden"></i><span class="hidden sm:inline">{{ t('Visual') }}</span></button><button id="modeMd" role="tab" :aria-selected="ed.shownMode === 'md'" title="Markdown" :class="mc(ed.shownMode === 'md')" @click="ed.setMode('md')"><i class="fab fa-markdown sm:hidden"></i><span class="hidden sm:inline">Markdown</span></button><button id="modeFountain" role="tab" :aria-selected="ed.shownMode === 'fountain'" :title="t('Screenplay in Fountain format')" :class="mc(ed.shownMode === 'fountain')" @click="ed.setMode('fountain')"><i class="fas fa-film sm:hidden"></i><span class="hidden sm:inline">Fountain</span></button></div>
        <span id="scriptStatus" class="hidden sm:inline text-[11px] text-gray-500 whitespace-nowrap">{{ ed.status }}</span>
        <button id="cmtBtn" type="button" :title="t('Comments')" :class="['xl:hidden relative text-gray-400 hover:text-brand', { hidden: ed.shownMode !== 'visual' }]" @click="comments.toggle()"><i class="fas fa-comments"></i><span id="cmtCount" :class="['absolute -top-2 -right-2.5 min-w-[1rem] h-4 px-1 rounded-full bg-brand text-white text-[9px] font-bold items-center justify-center', comments.view && comments.view.open.length ? 'flex' : 'hidden']">{{ comments.view ? comments.view.open.length : '' }}</span></button>
        <button :title="t('Save as template')" class="text-gray-400 hover:text-brand" @click="ed.saveTemplate()"><i class="fas fa-bookmark"></i></button>
        <button :title="t('Delete script')" class="text-gray-400 hover:text-brand" @click="ed.removeScript()"><i class="fas fa-trash"></i></button>
      </div>
      <ScriptTabs />
      <EditorToolbar />
      <!-- Writing area: tabs and heading outline on the left (from 1024 px; the tabs above the outline up to 1679 px, then
           beside it) + "page" sheet in the middle + comments on the right. Below 1024 px the left box is `contents`, so its
           hidden children leave no gap (the outline still opens over the page from #tocBtn) -->
      <div id="scriptVisualWrap" ref="visualWrap" role="tabpanel" class="script-desk flex-1 overflow-y-auto" @scroll.passive="onScroll">
        <div class="flex justify-center gap-6 px-0 md:px-6 md:py-8">
          <div class="contents lg:flex lg:flex-col min-[1680px]:flex-row gap-6 flex-shrink-0 self-start sticky top-6">
            <ScriptTabs side />
            <ScriptToc />
          </div>
          <div ref="pageSlot" v-once class="contents"></div>
          <CommentsPanel />
        </div>
      </div>
      <FountainEditor role="tabpanel" />
      <!-- The Markdown tab panel: a box around the field, so the field keeps its own textbox role -->
      <div ref="mdWrap" role="tabpanel" class="hidden flex-1 min-h-0 flex">
        <textarea id="scriptMd" ref="md" spellcheck="false" class="hidden select-text flex-1 w-full max-w-3xl mx-auto p-6 bg-transparent text-gray-100 font-mono text-sm leading-relaxed focus:outline-none resize-none" @input="ed.queueSave()" @keydown="mdKey"></textarea>
      </div>
      <div class="flex items-center justify-between px-4 md:px-6 py-2.5 border-t border-gray-800 bg-darkcard text-xs text-gray-400">
        <span id="scriptStats" class="min-w-0 truncate whitespace-nowrap">{{ ed.stats }}</span>
        <div class="flex gap-2 flex-shrink-0">
          <button :title="t('Teleprompter')" class="px-3 py-1.5 rounded-lg bg-darkbtn hover:text-gray-100" @click="prompter.openFromEditor()"><i class="fas fa-tv sm:mr-1"></i><span class="hidden sm:inline">{{ t('Teleprompter') }}</span></button>
          <button :title="t('Copy')" class="px-3 py-1.5 rounded-lg bg-darkbtn hover:text-gray-100" @click="ed.copy()"><i class="fas fa-copy sm:mr-1"></i><span class="hidden sm:inline">{{ t('Copy') }}</span></button>
          <button v-if="ed.shownMode === 'fountain'" :title="t('Download')" class="px-3 py-1.5 rounded-lg bg-darkbtn hover:text-gray-100" @click="ed.exportFountain()"><i class="fas fa-download mr-1"></i>.fountain</button>
          <button v-else :title="t('Download')" class="px-3 py-1.5 rounded-lg bg-darkbtn hover:text-gray-100" @click="ed.exportMd()"><i class="fas fa-download mr-1"></i>.md</button>
        </div>
      </div>
    </div>
  </Teleport>
  <ToolbarMenu />
</template>
